import React, { useState } from 'react';

const S = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    animation: 'sectionReveal 0.4s ease both',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  headerIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
  },
  headerText: {
    fontSize: 13,
    color: '#555',
  },
  headerBold: {
    fontWeight: 700,
    color: '#1B5E3B',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#2E7D52',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  card: {
    background: '#fff',
    border: '1.5px solid #C8EDD6',
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(27,94,59,0.08)',
  },
  cardHeader: {
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 50%, #2E7D52 100%)',
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardTitle: {
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.01em',
  },
  codeChip: {
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 20,
    letterSpacing: '0.05em',
  },
  cardBody: {
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  fieldFull: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    gridColumn: '1 / -1',
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#888',
  },
  fieldValue: {
    fontSize: 13,
    color: '#1a2e1a',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  gstChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: '#C8EDD6',
    color: '#0A3D1F',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 20,
  },
  divider: {
    height: 1,
    background: '#f0f7f0',
    margin: '4px 0',
  },
  contactsSection: {
    background: '#f6fcf8',
    borderRadius: 10,
    padding: '10px 14px',
    border: '1px solid #C8EDD6',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: '1px solid #e8f5ee',
    gap: 12,
  },
  contactRowLast: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '6px 0',
    gap: 12,
  },
  contactName: {
    fontWeight: 600,
    fontSize: 13,
    color: '#1a2e1a',
  },
  contactDesig: {
    fontSize: 11,
    color: '#2E7D52',
    fontWeight: 500,
  },
  contactMeta: {
    fontSize: 12,
    color: '#555',
    textAlign: 'right',
  },
  branchChip: {
    background: '#e8f5ee',
    border: '1px solid #C8EDD6',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
  },
  emptyNote: {
    fontSize: 12,
    color: '#aaa',
    fontStyle: 'italic',
  },
  noResults: {
    background: '#fff8f0',
    border: '1.5px solid #ffe0b2',
    borderRadius: 14,
    padding: '20px 24px',
    fontSize: 14,
    color: '#e65100',
    textAlign: 'center',
  },
};

function FieldRow({ label, value, full }) {
  if (!value) return null;
  return (
    <div style={full ? S.fieldFull : S.field}>
      <span style={S.fieldLabel}>{label}</span>
      <span style={S.fieldValue}>{value}</span>
    </div>
  );
}

function formatAddress(c) {
  const parts = [c.address, c.city, c.state, c.pincode].filter(Boolean);
  return parts.join(', ') || null;
}

function EntityCard({ entity, type }) {
  const [showBranches, setShowBranches] = useState(false);

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <span style={S.cardTitle}>{entity.name}</span>
        <span style={S.codeChip}>{type === 'customer' ? 'Customer' : 'Vendor'} · {entity.code}</span>
      </div>
      <div style={S.cardBody}>
        <div style={S.fieldGrid}>
          <FieldRow label="Phone" value={entity.phone} />
          <FieldRow label="Email" value={entity.email} />
          <FieldRow label="Address" value={formatAddress(entity)} full />
          <FieldRow label="Website" value={entity.website} />
          <div style={S.field}>
            <span style={S.fieldLabel}>GST Status</span>
            {entity.gst_status
              ? <span style={S.gstChip}>✓ {entity.gst_status}</span>
              : <span style={{ ...S.fieldValue, color: '#aaa' }}>—</span>}
          </div>
        </div>

        {/* Contact Persons */}
        {entity.contacts?.length > 0 && (
          <>
            <div style={S.divider} />
            <div>
              <div style={{ ...S.sectionLabel, fontSize: 10, marginBottom: 6 }}>
                Contact Persons ({entity.contacts.length})
              </div>
              <div style={S.contactsSection}>
                {entity.contacts.map((p, i) => (
                  <div key={i} style={i === entity.contacts.length - 1 ? S.contactRowLast : S.contactRow}>
                    <div>
                      <div style={S.contactName}>{p.name}</div>
                      {p.designation && <div style={S.contactDesig}>{p.designation}</div>}
                      {p.dept && <div style={{ fontSize: 11, color: '#888' }}>{p.dept}</div>}
                    </div>
                    <div style={S.contactMeta}>
                      {p.mobile && <div>{p.mobile}</div>}
                      {p.email && <div style={{ color: '#2E7D52' }}>{p.email}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Branches with GSTIN (customers only) */}
        {entity.branches?.length > 0 && (
          <>
            <div style={S.divider} />
            <div>
              <div
                style={{ ...S.sectionLabel, fontSize: 10, marginBottom: 6, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowBranches(p => !p)}
              >
                Branches / GSTIN ({entity.branches.length}) {showBranches ? '▲' : '▼'}
              </div>
              {showBranches && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {entity.branches.map((b, i) => (
                    <div key={i} style={S.branchChip}>
                      <div style={{ fontWeight: 600, color: '#1a2e1a', marginBottom: 2 }}>{b.name}</div>
                      <div style={{ color: '#2E7D52', fontWeight: 700, fontSize: 11 }}>GSTIN: {b.gstin}</div>
                      {b.address && <div style={{ color: '#666', marginTop: 2 }}>{[b.address, b.city, b.state].filter(Boolean).join(', ')}</div>}
                      {b.contact_person && <div style={{ color: '#555', marginTop: 2 }}>Contact: {b.contact_person} {b.contact_no ? `· ${b.contact_no}` : ''}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ContactCard({ data }) {
  const { search_term, customers = [], vendors = [], total } = data;

  if (total === 0) {
    return (
      <div style={S.noResults}>
        No customer or vendor found matching "<strong>{search_term}</strong>".<br />
        <span style={{ fontSize: 12, color: '#aaa' }}>Try a partial name or check the spelling.</span>
      </div>
    );
  }

  return (
    <div style={S.wrapper}>
      <div style={S.header}>
        <div style={S.headerIcon}>📇</div>
        <div style={S.headerText}>
          Found <span style={S.headerBold}>{total} result{total !== 1 ? 's' : ''}</span> for "{search_term}"
          {customers.length > 0 && vendors.length > 0 && (
            <span> · {customers.length} customer{customers.length !== 1 ? 's' : ''}, {vendors.length} vendor{vendors.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {customers.length > 0 && (
        <div>
          {customers.length > 0 && vendors.length > 0 && (
            <div style={S.sectionLabel}>Customers</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {customers.map(c => <EntityCard key={c.code} entity={c} type="customer" />)}
          </div>
        </div>
      )}

      {vendors.length > 0 && (
        <div>
          {customers.length > 0 && vendors.length > 0 && (
            <div style={{ ...S.sectionLabel, marginTop: 4 }}>Vendors</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {vendors.map(v => <EntityCard key={v.code} entity={v} type="vendor" />)}
          </div>
        </div>
      )}
    </div>
  );
}
